import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { AuthService } from '@vanilla/services/AuthService.js';
import { AzureAdAuthService } from '@vanilla/services/AzureAdAuthService.js';
import { AppConfigService } from '@vanilla/services/AppConfigService.js';
import { permissions } from '@vanilla/services/PermissionService.js';
import { RealtimeService } from '@vanilla/services/RealtimeService.js';
import { RoleMetadataService } from '@vanilla/services/RoleMetadataService.js';
import { CacheService } from '@vanilla/services/CacheService.js';
import { ReferenceDataStore } from '@vanilla/reference/ReferenceDataStore.js';
import { CrmApiRepository } from '@vanilla/services/crm/CrmApiRepository.js';
import { clearSessionRoute } from '../shell/routeAccess.js';

const AuthContext = createContext(null);

/** After SSO, drop /auth/callback (and leftover OAuth query params). */
function normalizeAuthCallbackUrl() {
    const path = (window.location.pathname || '/').replace(/\/+$/, '') || '/';
    const search = window.location.search || '';
    if (path === '/auth/callback') {
        window.history.replaceState(null, '', '/');
        return;
    }
    if (/[?&](code|id_token|client_info)=/.test(search)) {
        window.history.replaceState(null, '', path || '/');
    }
}

function setSsoReturningClass(pending) {
    if (pending) {
        document.documentElement.classList.add('sso-returning');
    } else {
        document.documentElement.classList.remove('sso-returning');
    }
}

async function bootSessionServices() {
    await permissions.reload();
    await RoleMetadataService.ensureLoaded().catch(() => null);
    RealtimeService.connect();
}

function teardownSessionServices() {
    permissions.reset();
    try { CacheService.clear(); } catch { /* ignore */ }
    try { ReferenceDataStore.clear(); } catch { /* ignore */ }
    try { CrmApiRepository.clearCache(); } catch { /* ignore */ }
    clearSessionRoute();
    RealtimeService.disconnect();
}

export function AuthProvider({ children }) {
    const [session, setSession] = useState(null);
    const [bootstrapping, setBootstrapping] = useState(true);
    const [ssoPending, setSsoPending] = useState(() => AzureAdAuthService.isRedirectReturn());
    const [initializing, setInitializing] = useState(false);
    const [error, setError] = useState('');
    const [permsReady, setPermsReady] = useState(false);

    useEffect(() => {
        const onAuthChanged = (e) => {
            setSession(e.detail?.user || AuthService.getSession());
            setError('');
        };
        window.addEventListener('authChanged', onAuthChanged);
        return () => window.removeEventListener('authChanged', onAuthChanged);
    }, []);

    // Boot: settle MSAL redirect (only on real Entra return), then validate session.
    // Do NOT gate with a "started" ref — React Strict Mode remounts once; a ref that
    // blocks the second run leaves bootstrapping/ssoPending stuck forever after the
    // first run is cancelled post-/config.
    useEffect(() => {
        let cancelled = false;

        (async () => {
            const returning = AzureAdAuthService.isRedirectReturn();
            if (returning) {
                setSsoPending(true);
                setSsoReturningClass(true);
            } else {
                // Cold load: never stay on SSO spinner if index.html left a stale class
                setSsoPending(false);
                setSsoReturningClass(false);
            }

            await AppConfigService.load().catch(() => null);
            if (cancelled) return;

            try {
                let msSession = null;
                // MSAL only when Azure AD is enabled AND this is a real redirect return.
                // Plain cold load (azureAdEnabled true or false) never touches MSAL.
                if (AppConfigService.isAzureAdEnabled() && returning) {
                    msSession = await AuthService.completeMicrosoftRedirect();
                }
                if (cancelled) return;

                normalizeAuthCallbackUrl();

                if (msSession) {
                    setSession(msSession);
                    return;
                }

                const restored = await AuthService.restoreSession();
                if (cancelled) return;
                if (restored) {
                    setSession(restored);
                } else {
                    setSession(null);
                }
            } catch (err) {
                if (cancelled) return;
                console.error('[AuthContext] auth boot failed', err);
                normalizeAuthCallbackUrl();
                setError(err?.message || 'Sign-in failed.');
                setSession(null);
            } finally {
                // Always clear SSO gate when this boot attempt finishes (success, fail, or timeout).
                // Cancelled Strict Mode runs skip state updates; the remounted effect owns cleanup.
                if (!cancelled) {
                    setSsoPending(false);
                    setSsoReturningClass(false);
                    setBootstrapping(false);
                }
            }
        })();

        return () => { cancelled = true; };
    }, []);

    // Boot permissions + realtime when session becomes active (incl. page refresh).
    useEffect(() => {
        let cancelled = false;
        const active = !!session?.userId && session.status === 'active';
        if (!active) {
            setPermsReady(false);
            return undefined;
        }
        (async () => {
            try {
                await bootSessionServices();
            } catch (err) {
                console.error('[AuthContext] session boot failed', err);
            } finally {
                if (!cancelled) {
                    setPermsReady(true);
                    window.dispatchEvent(new CustomEvent('navChanged', { detail: { route: null } }));
                }
            }
        })();
        return () => { cancelled = true; };
    }, [session?.userId, session?.status]);

    const login = useCallback(async (email, password) => {
        setError('');
        setInitializing(true);
        try {
            const s = await AuthService.login(email, password);
            setSession(s);
            return s;
        } catch (err) {
            setError(err?.message || 'Login failed. Please try again.');
            throw err;
        } finally {
            setInitializing(false);
        }
    }, []);

    const loginWithMicrosoft = useCallback(async () => {
        setError('');
        setSsoPending(true);
        setSsoReturningClass(true);
        try {
            await AuthService.loginWithMicrosoft();
            // Successful redirect navigates away; leave ssoPending until unload.
        } catch (err) {
            setSsoPending(false);
            setSsoReturningClass(false);
            setError(err?.message || 'Microsoft sign-in is not configured.');
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await AuthService.logout();
        } finally {
            teardownSessionServices();
            setSession(null);
            setPermsReady(false);
        }
    }, []);

    const isAuthenticated = !!session && !!session.userId && session.status === 'active';

    const value = useMemo(() => ({
        session,
        isAuthenticated,
        bootstrapping,
        ssoPending,
        initializing,
        error,
        login,
        loginWithMicrosoft,
        logout,
        permsReady,
    }), [
        session,
        isAuthenticated,
        bootstrapping,
        ssoPending,
        initializing,
        error,
        login,
        loginWithMicrosoft,
        logout,
        permsReady,
    ]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
